import React from 'react'
import { Hash, FileText, Calendar, Edit, Eye, Download } from 'lucide-react'
import { Badge } from '../ui/Badge.js'
import { Button } from '../ui/Button.js'
import type { OrderResponse } from '../../contracts/orders.js'
import { formatCurrency } from '../../lib/orders-utils.js'
import { formatDisplayDate, calculateDeliveryEstimate } from '../../lib/date-utils.js'
import { useThemeTokens } from '../../themes/index.js'

interface OrderRowProps {
  order: OrderResponse
  onEdit: () => void
  onViewDetails: () => void
  onDownloadPDF: () => void
}

// Utility function to calculate days old from API date format
const calculateDaysOld = (createdAt: string): number => {
  const orderDate = new Date(createdAt)
  const today = new Date()
  const diffTime = Math.abs(today.getTime() - orderDate.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

// Map API status to badge variants
const getStatusVariant = (status: OrderResponse['status']) => {
  switch (status) {
    case 'pending': return 'draft'
    case 'active': return 'submitted'
    case 'completed': return 'completed'
    case 'cancelled': return 'completed'
    default: return 'default'
  }
}

export const OrderRow: React.FC<OrderRowProps> = ({ order, onEdit, onViewDetails, onDownloadPDF }) => {
  const theme = useThemeTokens()
  const [isPreviewHovered, setIsPreviewHovered] = React.useState(false)

  const daysOld = calculateDaysOld(order.created_at)

  // Calculate delivery estimate if not provided
  const deliveryEstimate = order.delivery_date || calculateDeliveryEstimate(order.created_at)

  // Extract project name (prefer project.name, fallback to title)
  const projectName = order.project?.name || order.title

  // Extract PO number from notes (fallback to empty string)
  const purchaseOrderNumber = order.notes?.replace('PO: ', '') || ''

  // Use total_cost if available, fallback to amount
  const totalAmount = order.total_cost || order.amount

  // Table cell base styles
  const tableCellStyles: React.CSSProperties = {
    padding: theme.spacing.md,
    borderRight: `1px solid ${theme.colors.border}`,
    verticalAlign: 'top'
  }

  // Preview Panel Styles (now for table cell)
  const previewPanelStyles: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.sm,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    height: '100%',
    minHeight: '80px'
  }

  // Preview Panel Hover Styles
  const previewPanelHoverStyles: React.CSSProperties = {
    ...previewPanelStyles,
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.primary,
    boxShadow: theme.shadows.sm
  }



  // Preview Item Row Styles
  const previewItemStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    color: theme.colors.text,
    marginBottom: '2px'
  }

  return (
    <tr 
      style={{
        borderBottom: `1px solid ${theme.colors.border}`,
        transition: 'background-color 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.colors.background
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      {/* Order & Project Column */}
      <td style={tableCellStyles}>
        <div style={{
          fontSize: theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.text,
          marginBottom: theme.spacing.xs
        }}>
          Order #{order.id}
        </div>
        <div style={{
          fontSize: theme.typography.fontSize.md,
          color: theme.colors.text,
          marginBottom: theme.spacing.sm
        }}>
          {projectName}
        </div>
        <div style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.textSecondary
        }}>
          {daysOld} day{daysOld !== 1 ? 's' : ''} old
        </div>
      </td>

      {/* Status Column */}
      <td style={{
        ...tableCellStyles,
        textAlign: 'center',
        verticalAlign: 'middle'
      }}>
        <Badge variant={getStatusVariant(order.status)}>
          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
        </Badge>
      </td>

      {/* Details Column */}
      <td style={tableCellStyles}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginBottom: theme.spacing.xs,
          fontSize: theme.typography.fontSize.sm
        }}>
          <Hash size={14} color={theme.colors.textSecondary} />
          <span>Job: {order.job_number || 'N/A'}</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginBottom: theme.spacing.xs,
          fontSize: theme.typography.fontSize.sm
        }}>
          <FileText size={14} color={theme.colors.textSecondary} />
          <span>PO: {purchaseOrderNumber || 'N/A'}</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.textSecondary
        }}>
          <FileText size={14} color={theme.colors.textSecondary} />
          <span>{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}</span>
        </div>
      </td>

      {/* Dates Column */}
      <td style={tableCellStyles}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginBottom: theme.spacing.xs,
          fontSize: theme.typography.fontSize.sm
        }}>
          <Calendar size={14} color={theme.colors.textSecondary} />
          <span>Created: {formatDisplayDate(order.created_at)}</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          fontSize: theme.typography.fontSize.sm
        }}>
          <Calendar size={14} color={theme.colors.textSecondary} />
          <span>Est: {formatDisplayDate(deliveryEstimate)}</span>
        </div>
      </td>

      {/* Amount Column */}
      <td style={{
        ...tableCellStyles,
        textAlign: 'right',
        verticalAlign: 'middle'
      }}>
        <div style={{
          fontSize: theme.typography.fontSize.sm,
          color: theme.colors.textSecondary
        }}>
          {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
        </div>
        <div style={{
          fontSize: theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.success,
          marginBottom: theme.spacing.xs
        }}>
          {formatCurrency(totalAmount)}
        </div>
      </td>

      {/* Preview Column - The cool feature you wanted to keep! */}
      <td style={{
        ...tableCellStyles,
        width: '200px'
      }}>
        <div 
          style={isPreviewHovered ? previewPanelHoverStyles : previewPanelStyles}
          onClick={onViewDetails}
          onMouseEnter={() => setIsPreviewHovered(true)}
          onMouseLeave={() => setIsPreviewHovered(false)}
          title="Click to view order details"
        >
          {order.items && order.items.length > 0 ? (
            <>
              {order.items.slice(0, 3).map(item => (
                <div key={item.id} style={previewItemStyles}>
                  <span style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    maxWidth: '120px'
                  }}>
                    {item.product_name}
                  </span>
                  <span>×{item.quantity}</span>
                </div>
              ))}
              {order.items.length > 3 && (
                <div style={{
                  ...previewItemStyles,
                  color: theme.colors.textSecondary,
                  fontStyle: 'italic'
                }}>
                  <span>+{order.items.length - 3} more item{order.items.length - 3 !== 1 ? 's' : ''}</span>
                </div>
              )}
            </>
          ) : (
            <div style={{
              fontSize: '12px',
              color: theme.colors.textSecondary,
              fontStyle: 'italic'
            }}>
              No items added yet
            </div>
          )}
        </div>
      </td>

      {/* Actions Column */}
      <td style={{
        ...tableCellStyles,
        textAlign: 'center',
        verticalAlign: 'middle',
        borderRight: 'none'
      }}>
        <div style={{
          display: 'flex',
          gap: theme.spacing.xs,
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Button
            size="sm"
            variant="ghost"
            onClick={onEdit}
            style={{
              padding: theme.spacing.xs,
              minWidth: 'auto'
            }}
            title="Edit Order"
          >
            <Edit size={16} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onViewDetails}
            style={{
              padding: theme.spacing.xs,
              minWidth: 'auto'
            }}
            title="View Details"
            aria-label="View Details"
          >
            <Eye size={16} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDownloadPDF}
            style={{
              padding: theme.spacing.xs,
              minWidth: 'auto',
              color: '#059669'
            }}
            title="Download PDF"
          >
            <Download size={16} />
          </Button>
        </div>
      </td>
    </tr>
  )
} 